import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  attributeService,
  attributeValueService,
  fieldService,
  referenceService,
} from '../services';
import {
  AttributeSensitivity,
  canHaveAttributeValues,
  formatData,
  formatDatasetPath,
  formatPath,
  Role,
  Asset,
  handleError,
  resolveGlossaryAssetSubmit,
  getAssetSubtitle,
} from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  MorphButton,
  ObjectDetail,
  ObjectDetails,
  References,
  // SkeletonLoader,
  Tabs,
  TextareaReadOnly,
  textareaInitialState,
  fetchToReferencesCount,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useSearch,
  useGlobalState,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

export const Attribute = () => {
  const { queryParams } = useQueryParams();

  const [attribute, setAttribute] = useState([]);
  const [fields, setFields] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [toReferencesCount, setToReferencesCount] = useState({});
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFieldAssociations, setLoadingFieldAssociations] = useState(true);
  const [loadingMoreOptions, setLoadingMoreOptions] = useState(false);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({
    tab: queryParams.tab,
  });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { attribute_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Attribute,
    objectIds: [attribute_id],
  });
  const { appendStatus } = useStatus({
    setState: setAttributeValues,
    assetCategory: Asset.Glossary,
  });

  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchSearchResults,
    clearSearchResults,
    loadNextPage,
  } = useSearch({
    setLoading: setLoadingMoreOptions,
    objectName: Asset.Field,
    pagination: true,
    enableHistory: false,
  });

  useEffect(() => {
    fetchItems();
    fetchFields();
    fetchValues(attribute_id);
    fetchFromReferences();

    fetchToReferencesCount({
      showMessage,
      toObjectName: Asset.Attribute,
      toObjectId: attribute_id,
    })
      .then(res => setToReferencesCount(res))
      .catch(err => {
        showMessage(err, 'error');
      });
  }, [attribute_id]);

  const fetchItems = async () => {
    setLoading(true);
    attributeService
      .getById(attribute_id)
      .then(res => {
        if (res.attributes.length < 1) {
          setNotFound(true);
        } else {
          setAttribute(res.attributes[0]);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(() => setLoading(false));
  };

  const fetchValues = async id => {
    setLoading(true);
    attributeValueService
      .getByAttributeId(id)
      .then(res => {
        if (res) {
          const result = res.attribute_values.map(value => ({
            ...value,
            attribute_value_icon: 'attribute-value',
          }));

          setAttributeValues(result);
          appendStatus();
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      })
      .finally(res => setLoading(false));
  };

  const fetchFields = async () => {
    fieldService
      .getByAttributeId(attribute_id)
      .then(res => {
        if (res) {
          const result = res.fields.map(field => ({
            ...field,
            field_source_description:
              field.field_source_description_secondary || field.field_source_description,
            key_type: field.is_primary_key ? 'Primary Key' : '',
            // Render the field's dataset with its ancestors below it, so the parent context is not
            // lost -- the endpoint an API schema belongs to, the semantic model a table sits in. A
            // dataset that nests under nothing renders as its name alone.
            dataset_path: formatDatasetPath(field, { ancestors: field.ancestors }),
          }));
          setFields(result);
          appendStatus({ setState: setFields, assetCategory: Asset.System });
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(res => {
        setLoadingFieldAssociations(false);
      });
  };

  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.Attribute, attribute_id)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addFieldAssociation = async event => {
    let { name, value } = '';

    name = event.name;
    value = event.value;
    const attributeId = fields.find(x => x.field_id === value) ? null : attribute_id;

    const fieldsToAdd = {
      fields: [{ field_id: value, attribute_id: parseInt(attributeId) }],
    };

    await fieldService
      .updateFieldAssociation(fieldsToAdd)
      .then(async res => {
        fetchSearchResults(undefined, false);
        fetchFields();

        res && showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addAttributeValue = async data => {
    const attributeValues = {
      attribute_values: Array.isArray(data) ? data : [data],
    };

    await attributeValueService
      .create(attributeValues)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        res && showMessage(res.message);
        fetchValues(attribute_id);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editAttributeValue = async data => {
    const attributeValue = {
      attribute_values: Array.isArray(data) ? data : [data],
    };

    await attributeValueService
      .update(attributeValue)
      .then(async res => {
        fetchValues(attribute_id);
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Saves the edited asset. The form's role picker can turn this attribute into a Term, which creates
   * it in the entity table instead, so the target is resolved from the submitted row.
   */
  const editGlossaryAsset = async data => {
    const { service, payload, isCreate, isConversion, targetObjectName } =
      resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();

        // A conversion leaves nothing at this URL — the attribute has been replaced by a row in the
        // other table — so follow the asset instead of re-reading an id that no longer exists.
        if (isConversion) {
          const converted = res.entities?.[0] || res.attributes?.[0];
          if (converted) {
            navigate(
              targetObjectName === Asset.Entity
                ? `/glossaries/${converted.glossary_id}/${converted.entity_id}`
                : `/attributes/${converted.attribute_id}`
            );
            return;
          }
        }

        fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
        fetchFields();
        // Saving the attribute can add, change and delete its values -- they are edited in the same
        // dialog -- so the Values tab is re-read with everything else.
        fetchValues(attribute_id);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editField = async data => {
    const fieldsToUpdate = { fields: Array.isArray(data) ? data : [data] };

    await fieldService
      .update(fieldsToUpdate)
      .then(async res => {
        fetchFields();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteAttributeValue = async data => {
    await attributeValueService
      .destroy({ attribute_values: data })
      .then(async res => {
        showMessage(res.message);
        fetchValues(attribute_id);
        clearSelectedItems();
        hideModalDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
    return;
  };

  const deleteAttribute = async data => {
    await attributeService
      .destroy({ attributes: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the glossary the attribute belonged to, which shows it
          // in place in the tree, rather than to the flat list of every attribute.
          navigate(`/glossaries/${attribute.glossary_id}`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const removeFieldAssociation = async data => {
    let items = [];

    data.forEach(item => {
      items.push({ field_id: item.field_id, attribute_id: null });
    });

    const fieldsToRemove = { fields: items };
    await fieldService
      .updateFieldAssociation(fieldsToRemove)
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchFields();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = value => {
    setSearchTerm(value);
  };

  const loadMoreOptions = () => {
    loadNextPage();
  };

  const requestResults = searchResults.map(result => {
    const selectedByOther =
      result.searchable.attribute_id && result.searchable.attribute_id.toString() !== attribute_id
        ? true
        : false;
    return {
      value: result.searchable.field_id,
      label: result.searchable.field_name,
      icon: result.searchable.datatype_category,
      secondaryLabel: formatPath([
        result.searchable.system_name,
        result.searchable.datasource_name,
        result.searchable.dataset_group_name,
        result.searchable.dataset_name,
      ]),
      selectedByOther: selectedByOther,
      tooltip: selectedByOther
        ? `This Field is already associated with Attribute:\n${result.searchable.attribute_name}`
        : '',
    };
  });

  const data = loading ? [] : attribute;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Attribute"
          title={
            <span>
              {data.attribute_name} <span className="light-text">{data.attribute_unit_code}</span>
            </span>
          }
          statusId={data.status_id}
          subtitleIcon={<Icon name={data.attribute_role_name} tooltip={data.attribute_role_name} />}
          subtitleText={data && !loading && getAssetSubtitle(data, Asset.Attribute)}
          details={[
            ...renderCustomPropertyValues().details,
            {
              title: 'Last Modified',
              data: formatData(data.modified_timestamp, 'datetime'),
            },
            {
              title: 'Created',
              data: formatData(data.created_timestamp, 'datetime'),
            },
            {
              title: 'Id',
              data: data.attribute_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          description={data.attribute_description}
          fromReferences={fromReferences}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editAttribute', [data], editGlossaryAsset),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteAttribute', [data], deleteAttribute),
            },
          ]}
        >
          {data?.attribute_definition && (
            <ObjectDetail
              label="Definition"
              loading={loading}
              showLoadingForChildren={true}
              loadingChildrenCount={2}
            >
              <TextareaReadOnly
                value={textareaInitialState({
                  value: data.attribute_definition,
                  readOnly: true,
                  descriptions: fromReferences,
                })}
              />
            </ObjectDetail>
          )}

          <ObjectDetail label="Data Sensitivity" loading={loading} showLoadingForChildren={true}>
            <React.Fragment>
              {data.is_pii && (
                <Icon
                  name="pii"
                  tooltip="Classified as Personally Identifiable Information (PII)"
                />
              )}
              <Icon
                name="attribute_sensitivity"
                label={data.attribute_sensitivity_name}
                value={data.attribute_sensitivity_level}
                values={AttributeSensitivity}
              />
            </React.Fragment>
          </ObjectDetail>

          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={true}
        >
          <div
            label="Associated Fields"
            tabid="associated_fields"
            resultCount={
              loadingFieldAssociations || fields.length === 0 ? undefined : fields.length
            }
          >
            <DataTable
              columns={[
                { id: 'key_type', name: '', type: 'icon' },
                {
                  id: 'datatype_category',
                  name: '',
                  type: 'icon',
                  tooltip: 'datatype_fullname',
                },
                {
                  id: 'field_name',
                  name: 'Name',
                  link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
                  className: 'bold',
                  tooltip: 'field_source_description',
                },
                {
                  id: 'datatype_fullname',
                  name: 'Datatype',
                  className: 'small-text',
                  viewMode: 'statistics',
                },
                {
                  // Matches the field_source_description key built in fetchFields, which holds the
                  // field's own source description text — not the Attribute entity.
                  id: 'field_source_description',
                  name: 'Source Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'dataset_type_name',
                  name: '',
                  type: 'icon',
                  tooltip: 'dataset_type_name',
                  className: 'no-right-margin',
                },
                {
                  // dataset_path holds the formatDatasetPath() output (its segments link
                  // themselves), so this column carries no `link` of its own.
                  id: 'dataset_path',
                  name: 'Dataset',
                  sortField: 'dataset_name',
                },
                {
                  id: 'dataset_group_name',
                  name: 'Dataset Group',
                  link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                },
                {
                  id: 'datasource_type_category',
                  name: '',
                  type: 'icon',
                  tooltip: 'datasource_type_name',
                  className: 'no-right-margin',
                },
                {
                  id: 'datasource_name',
                  name: 'Datasource',
                  link: '/systems/:system_id/:datasource_id',
                },
                {
                  id: 'system_name',
                  name: 'System',
                  link: '/systems/:system_id',
                },
              ]}
              buttons={[
                {
                  customButton: (
                    <MorphButton
                      name="field_id"
                      value={fields.map(x => ({ value: x.field_id, label: x.field_name }))}
                      options={requestResults}
                      onChange={addFieldAssociation}
                      placeholder="Search Fields to add..."
                      onInputChange={handleInputChange}
                      loadMoreOptions={loadMoreOptions}
                      loading={loadingMoreOptions}
                      resultCount={searchResultsCount && searchResultsCount.field}
                      searchTerm={searchTerm}
                      clearSearchResults={clearSearchResults}
                      onToggle={clearSelectedItems}
                    />
                  ),
                  action: 'add',
                  onClick: () => {},
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items => showSideDialog('editField', items || selectedItems, editField),
                },
                {
                  label: 'Remove',
                  action: 'remove',
                  tooltip:
                    'Remove association with this Attribute. The Field itself will not be deleted.',
                  onClick: items =>
                    showModalDialog(
                      'removeFieldAssociation',
                      items || selectedItems,
                      removeFieldAssociation
                    ),
                },
              ]}
              data={fields}
              idColumn="field_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingFieldAssociations}
              editable={editable}
              filterable={true}
              filterObjects={[
                'datatype_fullname',
                'field_name',
                'dataset_name',
                'dataset_group_name',
                'datasource_name',
                'system_name',
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Values"
            tabid="values"
            disabled={!canHaveAttributeValues(data)}
            tooltip={
              !canHaveAttributeValues(data)
                ? {
                    title:
                      "Attribute Values can only be registered on Attributes with role 'Dimension'",
                  }
                : ''
            }
            resultCount={
              loading || attributeValues.length === 0 ? undefined : attributeValues.length
            }
          >
            <DataTable
              columns={[
                { id: 'attribute_value_icon', name: '', type: 'icon' },
                {
                  id: 'attribute_value_name',
                  name: 'Name',
                  link: '/attributes/:attribute_id/:attribute_value_id',
                  className: 'bold',
                },
                {
                  id: 'attribute_value_code',
                  name: 'Code',
                },
                {
                  id: 'attribute_value_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                ...(settings.SHOW_GLOSSARY_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  onClick: items => {
                    showSideDialog('addAttributeValue', items || selectedItems, addAttributeValue);
                  },
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items => {
                    showSideDialog(
                      'editAttributeValue',
                      items || selectedItems,
                      editAttributeValue
                    );
                  },
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  onClick: items =>
                    showModalDialog(
                      'deleteAttributeValue',
                      items || selectedItems,
                      deleteAttributeValue
                    ),
                },
              ]}
              data={attributeValues}
              idColumn="attribute_value_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loading}
              editable={editable}
              filterable={true}
              filterObjects={[
                'attribute_value_name',
                'attribute_value_code',
                'attribute_value_description',
                ...(settings.SHOW_GLOSSARY_STATUSES ? ['status_name'] : []),
              ]}
              showViewModes={true}
            />
          </div>

          <div
            label="Related Assets"
            tabid="related_assets"
            resultCount={toReferencesCount && toReferencesCount.total}
          >
            <References
              showMessage={showMessage}
              toObjectName={Asset.Attribute}
              toObjectId={attribute_id}
              toReferencesCount={toReferencesCount}
            />
          </div>
          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Attribute}
              objectId={attribute_id}
              filterObjects={[
                'changedData.new_data||old_data.attribute_name',
                'changedData.new_data||old_data.attribute_description',
                'changedData.new_data||old_data.attribute_definition',
                'changedData.new_data||old_data.attribute_role_name',
                'changedData.new_data||old_data.attribute_unit_name',
                'changedData.new_data||old_data.attribute_sensitivity_name',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
